import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import MyPageBanner from "../components/MyPageBanner";
import MyStandingsTable from "../components/MyStandingsTable";
import Partidos from "../components/Partidos";
import PartidosFinal from "../components/PartidosFinal";
import Goleo from "../components/Goleo";
import Equipos from "../components/Equipos";
import StateMessage from "../components/StateMessage";
import { supabase } from "../supabaseClient";
import "./DynamicLeaguePage.css";

const FRESH_MS = 60_000; // 1 min cache

function useIsMobile(maxWidth = 767.98) {
  const query = `(max-width: ${maxWidth}px)`;
  const getInitial = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  };

  const [isMobile, setIsMobile] = useState(getInitial);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mql = window.matchMedia(query);

    const onChange = () => setIsMobile(mql.matches);
    onChange();

    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }

    // Safari fallback
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return isMobile;
}

const DynamicLeaguePage = ({
  bannerImage,
  bannerText,
  bannerSubtitle,
  bannerMeta,
  leagueName,
  themeColor = "#121b69",
  trackGoals,
  trackPlayers,
  finalsStarted,
}) => {
  const [standings, setStandings] = useState([]);
  const [goleos, setGoleos] = useState({ men: [], women: [] });
  const [loaded, setLoaded] = useState(false);
  // Tabs below 1024px: tablets get one focused section at a time too.
  const isMobile = useIsMobile(1023.98);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const KEY = `leagueData:v3:${leagueName}`;
    const cached = (() => {
      try {
        return JSON.parse(sessionStorage.getItem(KEY) || "null");
      } catch {
        return null;
      }
    })();

    const now = Date.now();

    setLoaded(false);
    if (cached?.data) {
      setStandings(cached.data.standings || []);
      setGoleos(cached.data.goleos || { men: [], women: [] });
      setLoaded(true);
    }

    if (cached && now - cached.ts < FRESH_MS) return;

    const fetchLeagueData = async () => {
      try {
        const { data: standingsData, error: standingsError } = await supabase
          .from("teams")
          .select(
            "team_id:id, team_name, logo_url, primary_kit_colors, secondary_kit_colors, games_played, wins, draws, losses, goals_for, goals_against, points"
          )
          .eq("league_id", leagueName);

        if (standingsError) throw standingsError;

        const sortedStandings = (standingsData || []).sort((a, b) => {
          if (b.points !== a.points) return (b.points || 0) - (a.points || 0);
          const aGD = (a.goals_for || 0) - (a.goals_against || 0);
          const bGD = (b.goals_for || 0) - (b.goals_against || 0);
          if (bGD !== aGD) return bGD - aGD;
          return (b.goals_for || 0) - (a.goals_for || 0);
        });

        const formattedStandings = sortedStandings.map((t) => ({
          ...t,
          goal_difference: (t.goals_for || 0) - (t.goals_against || 0),
        }));

        let nextGoleos = { men: [], women: [] };

        if (trackGoals === 1 || trackGoals === true) {
          // Fetch top male scorers (exclude 0-goal players)
          const { data: maleData } = await supabase
            .from("player_teams")
            .select(
              `
              goals,
              players!inner(player_id:id, player_name, gender),
              teams!inner(team_name, league_id)
            `,
            )
            .eq("players.gender", "male")
            .eq("teams.league_id", leagueName)
            .gt("goals", 0)
            .order("goals", { ascending: false })
            .limit(10);

          // Fetch top female scorers (exclude 0-goal players)
          const { data: femaleData } = await supabase
            .from("player_teams")
            .select(
              `
              goals,
              players!inner(player_id:id, player_name, gender),
              teams!inner(team_name, league_id)
            `,
            )
            .eq("players.gender", "female")
            .eq("teams.league_id", leagueName)
            .gt("goals", 0)
            .order("goals", { ascending: false })
            .limit(10);

          const formatScorers = (data) =>
            (data || [])
              .filter((row) => (row.goals || 0) > 0)
              .slice(0, 10)
              .map((row) => ({
                player_id: row.players.player_id,
                player_name: row.players.player_name,
                team_name: row.teams.team_name,
                goals: row.goals,
              }));

          nextGoleos = {
            men: formatScorers(maleData),
            women: formatScorers(femaleData),
          };
        }

        setStandings(formattedStandings);
        setGoleos(nextGoleos);
        sessionStorage.setItem(
          KEY,
          JSON.stringify({
            data: { standings: formattedStandings, goleos: nextGoleos },
            ts: Date.now(),
          }),
        );
      } catch (err) {
        console.error("Error fetching league data:", err);
      } finally {
        setLoaded(true);
      }
    };

    fetchLeagueData();
  }, [leagueName, trackGoals]);

  const tracksGoals = trackGoals === 1 || trackGoals === true;
  const canShowGoleo =
    tracksGoals &&
    ((goleos.men && goleos.men.length > 0) ||
      (goleos.women && goleos.women.length > 0));

  const tabs = [
    { id: "partidos", label: "Partidos" },
    { id: "tabla", label: "Tabla" },
    { id: "equipos", label: "Equipos" },
    ...(tracksGoals ? [{ id: "goleo", label: "Goleo" }] : []),
  ];
  const requestedTab = searchParams.get("tab");
  const mobileTab = tabs.some((t) => t.id === requestedTab)
    ? requestedTab
    : "partidos";

  const selectTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "partidos") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  // Arrow-key movement between tabs (WAI-ARIA tabs pattern)
  const onTabKeyDown = (event) => {
    const idx = tabs.findIndex((t) => t.id === mobileTab);
    let nextIdx = null;
    if (event.key === "ArrowRight") nextIdx = (idx + 1) % tabs.length;
    if (event.key === "ArrowLeft") nextIdx = (idx - 1 + tabs.length) % tabs.length;
    if (event.key === "Home") nextIdx = 0;
    if (event.key === "End") nextIdx = tabs.length - 1;
    if (nextIdx === null) return;
    event.preventDefault();
    selectTab(tabs[nextIdx].id);
    document.getElementById(`league-tab-${tabs[nextIdx].id}`)?.focus();
  };

  const finals = !!finalsStarted && (
    <PartidosFinal leagueFilter={leagueName} themeColor={themeColor} isFinal={true} />
  );
  const schedule = <Partidos leagueFilter={leagueName} themeColor={themeColor} />;
  const table = (
    <MyStandingsTable
      standings={standings}
      leagueName={leagueName}
      themeColor={themeColor}
      loading={!loaded}
    />
  );
  const teams = (
    <Equipos
      teams={standings}
      leagueId={leagueName}
      themeColor={themeColor}
      loading={!loaded}
    />
  );

  return (
    <>
      <MyPageBanner imageUrl={bannerImage} text={bannerText} subtitle={bannerSubtitle}>
        {bannerMeta}
      </MyPageBanner>

      {isMobile ? (
        <div className="league-mobile">
          <div
            className="league-mobile-tabs"
            role="tablist"
            aria-label="Secciones de la liga"
            onKeyDown={onTabKeyDown}
          >
            {tabs.map((tab) => {
              const selected = mobileTab === tab.id;
              return (
                <button
                  key={tab.id}
                  id={`league-tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls={`league-panel-${tab.id}`}
                  tabIndex={selected ? 0 : -1}
                  className={`league-mobile-tab${selected ? " active" : ""}`}
                  onClick={() => selectTab(tab.id)}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div
            className="league-mobile-content"
            role="tabpanel"
            id={`league-panel-${mobileTab}`}
            aria-labelledby={`league-tab-${mobileTab}`}
          >
            {mobileTab === "partidos" && (
              <>
                {finals && <div className="section-container">{finals}</div>}
                <div className="section-container">{schedule}</div>
              </>
            )}

            {mobileTab === "tabla" && (
              <div className="section-container">{table}</div>
            )}

            {mobileTab === "equipos" && (
              <div className="section-container">{teams}</div>
            )}

            {mobileTab === "goleo" && (
              <div className="section-container">
                {canShowGoleo ? (
                  <Goleo goleos={goleos} themeColor={themeColor} />
                ) : (
                  <StateMessage title={loaded ? "Aún no hay goles registrados" : undefined}>
                    {loaded
                      ? "El goleo aparecerá cuando se registren goles en los partidos."
                      : "Cargando goleo…"}
                  </StateMessage>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="league-desktop">
          <div className="layout">
            <div className="left">
              {finals && <div className="section-container">{finals}</div>}
              <div className="section-container">{table}</div>
              {canShowGoleo && (
                <div className="section-container">
                  <Goleo goleos={goleos} themeColor={themeColor} />
                </div>
              )}
            </div>
            <div className="right">
              <div className="section-container">{schedule}</div>
            </div>
          </div>
          <div className="equipos-layout">
            <div className="section-container">{teams}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default DynamicLeaguePage;
